import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy } from 'lucide-react';
import {
  ArenaChallenge,
  ArenaMemberProfile,
  ArenaTeam,
  fetchMemberProfile,
} from '../../../api/group/arena.api';
import ArenaPostPicker from './arena-post-picker';
import { formatVn } from '../arena-format';

interface Props {
  challenge: ArenaChallenge;
  team: ArenaTeam;
  myUserId: string;
  busy: boolean;
  onLeave: () => void;
  onKick: (userId: string) => void;
  onSubmit: (postId: string) => void;
}

const BTN = 'rounded-full px-4 py-2 text-[14px] font-medium transition disabled:opacity-60';

const ArenaTeamPanel: React.FC<Props> = ({ challenge, team, myUserId, busy, onLeave, onKick, onSubmit }) => {
  const [profiles, setProfiles] = useState<Record<string, ArenaMemberProfile>>({});
  const [picking, setPicking] = useState(false);

  const idsKey = team.members.map((m) => m.userId).join(',');
  useEffect(() => {
    let alive = true;
    team.members.forEach((m) => {
      fetchMemberProfile(m.userId)
        .then((p) => alive && setProfiles((prev) => ({ ...prev, [m.userId]: p })))
        .catch(() => undefined);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const isLeader = team.leaderUserId === myUserId;
  const enough = team.members.length >= challenge.teamSizeMin;
  const canSubmit = challenge.isOpen && isLeader && enough;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(team.inviteCode || '');
      toast.success('Đã sao chép mã mời');
    } catch {
      toast.error('Không sao chép được, hãy chép tay mã mời');
    }
  };

  const nameOf = (userId: string) =>
    userId === myUserId ? 'Bạn' : profiles[userId]?.fullName || `Thành viên …${userId.slice(-4)}`;

  return (
    <section className="flex flex-col gap-4 rounded-[16px] border border-[#DEE0E6] p-5 dark:border-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[18px] font-semibold text-[#19052f] dark:text-slate-100">Đội của bạn: {team.name}</h2>
        <span className="text-[13px] text-[#657384] dark:text-slate-400">
          {team.members.length} / {challenge.teamSizeMax} thành viên
        </span>
      </div>

      {team.inviteCode && (
        <div className="flex items-center gap-2 text-[14px] text-[#19052f] dark:text-slate-100">
          <span>Mã mời:</span>
          <code className="rounded bg-[#F1F5F9] px-2 py-1 font-semibold tracking-wider dark:bg-slate-800">
            {team.inviteCode}
          </code>
          <button
            type="button"
            aria-label="Chép mã mời"
            className="rounded-full border border-[#DEE0E6] p-2 text-[#475569] dark:border-slate-700 dark:text-slate-300"
            onClick={copyCode}
          >
            <Copy size={14} />
          </button>
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {team.members.map((m) => (
          <li
            key={m.userId}
            className="flex items-center justify-between gap-3 rounded-[12px] border border-[#DEE0E6] px-3 py-2 dark:border-slate-700"
          >
            <div className="flex min-w-0 items-center gap-2">
              {profiles[m.userId]?.avatar ? (
                <img src={profiles[m.userId].avatar} alt="" className="size-8 rounded-full object-cover" />
              ) : (
                <span className="flex size-8 items-center justify-center rounded-full bg-[#E2E8F0] text-[12px] font-semibold text-[#475569] dark:bg-slate-700 dark:text-slate-200">
                  {nameOf(m.userId).slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="truncate text-[14px] text-[#19052f] dark:text-slate-100">{nameOf(m.userId)}</span>
              {m.isLeader && (
                <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[11px] text-[#B45309]">Đội trưởng</span>
              )}
            </div>
            {isLeader && !m.isLeader && challenge.isOpen && (
              <button
                type="button"
                disabled={busy}
                className={`${BTN} border border-[#E11D48] py-1 text-[13px] text-[#E11D48]`}
                onClick={() => window.confirm('Mời thành viên này ra khỏi đội?') && onKick(m.userId)}
              >
                Mời ra
              </button>
            )}
          </li>
        ))}
      </ul>

      {!enough && (
        <p className="text-[14px] text-[#B45309] dark:text-amber-400">
          Đội cần ít nhất {challenge.teamSizeMin} người mới nộp được bài.
        </p>
      )}

      {team.entry && (
        <div className="rounded-[12px] bg-[#F1F5F9] px-3 py-2 text-[14px] text-[#19052f] dark:bg-slate-800 dark:text-slate-100">
          Bài đã nộp lúc {formatVn(team.entry.submittedAt)}.{' '}
          <a className="text-[#066bff] underline" href={`/share/post/${team.entry.postId}`} target="_blank" rel="noreferrer">
            Xem bài
          </a>
          {team.entry.disqualified && (
            <span className="ml-2 text-[#E11D48]">Bị loại{team.entry.disqualifyReason ? `: ${team.entry.disqualifyReason}` : ''}</span>
          )}
        </div>
      )}

      {picking && canSubmit && (
        <ArenaPostPicker
          memberIds={team.members.map((m) => m.userId)}
          startAt={challenge.startAt}
          busy={busy}
          onPick={(id) => {
            onSubmit(id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {canSubmit && !picking && (
          <button type="button" disabled={busy} className={`${BTN} bg-[#066bff] text-white`} onClick={() => setPicking(true)}>
            Chọn bài dự thi
          </button>
        )}
        {challenge.isOpen && (
          <button
            type="button"
            disabled={busy}
            className={`${BTN} border border-[#E11D48] text-[#E11D48]`}
            onClick={() => window.confirm('Rời khỏi đội này?') && onLeave()}
          >
            Rời đội
          </button>
        )}
      </div>
    </section>
  );
};

export default ArenaTeamPanel;
